import moment from "moment";

import { GeneratedAttachmentResponse } from "@/Common/Api/Attachments/Dto/Responses/GeneratedAttachmentResponse";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { TextAreaAutoSize } from "@/Common/Components/Inputs/TextAreaAutoSize";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { Popup } from "@/Common/Components/Modals/Popup";
import { ClinicianSearchStrategy, PersonSearchAutocompleteCustom } from "@/Common/Components/Search/PersonSearchAutocompleteCustom";
import { DocumentTreeClassControl } from "@/Common/Helpers/DocumentTreeClassControl";
import { MyleStaticResources } from "@/Common/Localization/MyleStaticResouces";
import { DocumentClassModel } from "@/Common/Models/DocumentClassModel";
import { EnumMyleLoginPermission } from "@/Common/Models/EnumMyleLoginPermission";
import { Person } from "@/Common/Models/Person/Person";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { PersonDropdownItem } from "@/Common/Models/UI/Inputs/PersonDropdownItem";
import { DocumentImportInfo } from "@/Communications/Models/DocumentImportInfo";

interface IDocumentImportPopupComponentProps {
    patientContactId?: string;
    generatedAttachment?: GeneratedAttachmentResponse;
    siteId?: string;
    responsibleClinicianContactFacetIds?: string[];
    source?: string;
    documentDate?: Date;

    cancelLabel: string;
    hideHeaderCloseOption?: boolean;
    top?: number;
    width?: number;

    onClose?: () => void;
    onImport?: (documnentImportInfo: DocumentImportInfo) => void;
    onImportPlusApprove?: (documnentImportInfo: DocumentImportInfo) => void;
}

class DocumentImportPopupComponentState {
    public responsibleClinicianContactIds: string[];
    public classLookupId: string;
    public subClassLookupId: string;
    public source: string;
    public description: string;
    public documentDate: Date;
    public note: string;

    public isImporting: boolean;
}

class DocumentImportPopupComponent extends BaseReactComponent<IDocumentImportPopupComponentProps, DocumentImportPopupComponentState> {
    private readonly componentId: string = "DocumentImportFormComponent";

    private activeClinicians: Person[];
    private sources: SearchableItemModelT<string>[];
    private descriptions: SearchableItemModelT<string>[];
    private classes: DocumentClassModel[];

    public static get hasImportDocumentPermission(): boolean {
        return sessionStore.hasPermission(EnumMyleLoginPermission.PatientTabPermission)
            || sessionStore.hasPermission(EnumMyleLoginPermission.DocumentTabPermission);
    }

    constructor(props: IDocumentImportPopupComponentProps) {
        super(props);

        this.initPrivateMembers();

        const responsibleClinicianContactIds = props.responsibleClinicianContactFacetIds
            ? this.activeClinicians
                .filter(p =>
                    props.responsibleClinicianContactFacetIds.includes(p.ContactFacetId)
                )
                .map(p => p.ContactId)
            : [];

        const firstClass = this.classes.firstOrDefault();

        const initState = new DocumentImportPopupComponentState();
        initState.responsibleClinicianContactIds = responsibleClinicianContactIds;
        initState.classLookupId = firstClass?.LookupId;
        initState.subClassLookupId = firstClass?.SubClasses?.first()?.LookupId;
        initState.source = props.source;
        initState.documentDate = props.documentDate;

        this.state = initState;
    }

    public componentDidMount(): void {
        this.setOverlay(this.componentId);
    }

    public render(): JSX.Element {
        return (
            <Popup
                title={cacheStore.GetString("ImportDocumentLabel")}
                width={this.width}
                topPosition={this.top}
                centeredHorizontally
                onCloseDelegate={() => this.close()}
                draggable={false}
                hideX={this.props.hideHeaderCloseOption ?? false}
            >
                <div className="p-body" style={{ maxHeight: "600px", paddingRight: "50px" }}>
                    {this.renderBody()}
                </div>
                <div className="p-footer">
                    {this.renderFooter()}
                </div>
            </Popup>
        );
    }

    private renderRow(label: string = null, field: JSX.Element = null): JSX.Element {
        return (
            <div className="row" style={{ marginBottom: "7px", marginTop: "7px" }}>
                <div className="col-xs-3 text-right" style={{ height: "40px", lineHeight: "40px", whiteSpace: "nowrap" }}>
                    {label ? cacheStore.GetString(label) : ""}
                </div>
                <div className="col-xs-8">{field}</div>
            </div>
        );
    }

    public renderBody(): JSX.Element {
        return (
            <div id={this.componentId} className="container-fluid">
                {this.renderResponsibleProviderRow()}
                {this.renderClassRow()}
                {this.renderSourceRow()}
                {this.renderDescriptionRow()}
                {this.renderDocumentDateRow()}
                {this.renderNoteRow()}
            </div>
        );
    }

    private renderResponsibleProviderRow(): JSX.Element {
        let selectedClinicians = this.activeClinicians.filter(x => this.state.responsibleClinicianContactIds.any(contactId => contactId === x.ContactId));

        return this.renderRow(
            "ResponsibleProvidersLabel",
            <PersonSearchAutocompleteCustom
                componentId="responsible_clinicians"
                selectedItemList={Immutable.List(selectedClinicians)}
                onSelectionCallback={personItem => this.handleOnResponsibleCliniciansChanged(personItem.Item.ContactId)}
                onClearCallback={(personItem: PersonDropdownItem) => this.mutateState(s => s.responsibleClinicianContactIds = s.responsibleClinicianContactIds.filter(p => p !== personItem.Item.ContactId))}
                focusOnLoad={false}
                closeDropDownAfterSelection={true}
                searchStrategy={new ClinicianSearchStrategy(this.activeClinicians)}
                allowMultipleItemSelection={true}
            />
        );
    }

    private renderClassRow(): JSX.Element {
        let selectedClassSubClass = JSON.stringify({ class: this.state.classLookupId, subclass: this.state.subClassLookupId });

        return this.renderRow(
            "ClassLabel",
            <select className="form-control myle-control minimal" onChange={e => this.handleOnClassChanged(e)} value={selectedClassSubClass}>
                {this.classes.map(classModel => (
                    <optgroup key={classModel.LookupId} label={cacheStore.GetLocalizedValueByLookupId(classModel.LookupId)}>
                        {classModel.SubClasses.toArray().map(subClass => {
                            let formattedOuput = cacheStore.GetLocalizedValueByLookupId(classModel.LookupId) + " - " + cacheStore.GetLocalizedValueByLookupId(subClass.LookupId);
                            return (
                                <option
                                    key={subClass.LookupId}
                                    value={JSON.stringify({ class: classModel.LookupId.toLocaleLowerCase(), subclass: subClass.LookupId.toLocaleLowerCase() })}
                                >
                                    {formattedOuput}
                                </option>
                            );
                        })}
                    </optgroup>
                ))}
            </select>
        );
    }

    private renderSourceRow(): JSX.Element {
        return this.renderRow(
            "SourceLabel",
            <TextboxControl
                autoCompleteItems={Immutable.List(this.sources)}
                value={this.state.source}
                onAutoCompleteSelectCallBack={(code, model, value) => this.mutateState(s => s.source = value)}
                onChange={value => this.mutateState(s => s.source = value)}
            />
        );
    }

    private renderDescriptionRow(): JSX.Element {
        return this.renderRow(
            "DescriptionDLabel",
            <TextboxControl
                autoCompleteItems={Immutable.List(this.descriptions)}
                value={this.state.description}
                onAutoCompleteSelectCallBack={(code, model, value) => this.mutateState(s => s.description = value)}
                onChange={value => this.mutateState(s => s.description = value)}
            />
        );
    }

    private renderDocumentDateRow(): JSX.Element {
        return this.renderRow(
            "DocumentDateLabel",
            <div>
                <div style={{ display: "inline-flex" }}>
                    <DateSelector
                        id="doc_create_date"
                        cssClass=""
                        date={this.state.documentDate}
                        dateFormat="dddd, MMM dd, yyyy"
                        onChangeCallback={date => this.mutateState(s => s.documentDate = date)}
                    />
                </div>

                <a
                    style={{ marginTop: "9px", marginLeft: "8px", cursor: "pointer" }}
                    id="dp1-today"
                    onClick={() => this.mutateState(s => s.documentDate = moment().toDate())}
                >
                    {cacheStore.GetString("TodayLabel")}
                </a>
            </div>
        );
    }

    private renderNoteRow(): JSX.Element {
        return this.renderRow(
            "NotesLabel",
            <TextAreaAutoSize
                cssClass="form-control"
                id="doc_create_note"
                value={this.state.note}
                onTextChangeCallback={value => this.mutateState(s => s.note = value)}
                minHeight="100px"
            />
        );
    }

    private renderFooter(): JSX.Element {
        return (
            <div className="col-lg-12 text-right">
                <button onClick={() => this.close()} className="btn btn-default push-right-5">{cacheStore.GetString(this.props.cancelLabel)}</button>
                <button className="btn btn-primary push-right-5" disabled={this.state.isImporting} onClick={() => this.handleImport()}>{cacheStore.GetString("ImportLabel")}</button>
                {sessionStore.canApproveDocument() &&
                    <button className="btn btn-primary" disabled={this.state.isImporting} onClick={() => this.handleImportPlusApprove()}>{cacheStore.GetString("ImportPlusApproveLabel")}</button>
                }
            </div>
        );
    }
    private get width(): number {
        return this.props.width || 1000;
    }

    private get top(): number {
        return this.props.top || 30;
    }

    private handleImport(): void {
        if (!this.props.onImport)
            return;

        this.mutateState(
            s => {
                s.isImporting = true;
            },
            () => this.props.onImport(this.initDocumentImportInfo(this.props, this.state))
        );
    }

    private handleImportPlusApprove(): void {
        if (!this.props.onImportPlusApprove)
            return;

        this.mutateState(
            s => {
                s.isImporting = true;
            },
            () => this.props.onImportPlusApprove(this.initDocumentImportInfo(this.props, this.state))
        );
    }

    private handleOnResponsibleCliniciansChanged(personContactId: string): void {
        if (!this.state.responsibleClinicianContactIds.any(x => x === personContactId))
            this.mutateState(s => s.responsibleClinicianContactIds.push(personContactId));
    }

    private handleOnClassChanged(e: any): void {
        let value = JSON.parse(e.target.value);
        this.mutateState(s => {
            s.classLookupId = value.class;
            s.subClassLookupId = value.subclass;
        });
    }

    private initPrivateMembers(): void {
        this.activeClinicians = cacheStore.GetClinicians(false) ?? [];
        this.sources = cacheStore.DocumentSources?.map(x => new SearchableItemModelT<string>(x));
        this.descriptions = new MyleStaticResources()
            .GetDocumentDescription()
            .map(x => new SearchableItemModelT<string>(x));
        this.classes = DocumentTreeClassControl.GetDocumentClasses().toArray();
    }

    private initDocumentImportInfo(props: IDocumentImportPopupComponentProps, state: DocumentImportPopupComponentState): DocumentImportInfo {
        let doc = new DocumentImportInfo(state);
        doc.patientContactId = props.patientContactId;
        doc.generatedAttachment = props.generatedAttachment;
        doc.siteId = props.siteId;
        return doc;
    }

    private close(): void {
        this.props.onClose && this.props.onClose();
    }
}

export { DocumentImportPopupComponent };
